import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  useTripsTrackingSearchParams,
} from 'hooks/store/trips/use-trips-tracking-search-params';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { MultiSelectGs, InputGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';
import { reportColumnOptions } from './data';

type Ref = {
  open: () => void;
  close: () => void;
}

export type OnSubmitRefineReportParams = {
  reportIdentifier?: string;
  reportColumns: string[];
}

type Props = {
  onSubmit: (params: OnSubmitRefineReportParams) => void;
  loading?: boolean;
}

export type RefineReportParamsDrawerRef = Ref

export const RefineReportParamsDrawer = React.forwardRef<Ref, Props>(({
                                                                        onSubmit,
                                                                        loading,
                                                                      }, ref) => {
  const [open, setOpen] = React.useState(false);
  const {
    reportIdentifier,
    reportColumns,
    updateReportIdentifier,
    updateReportColumns,
  } = useTripsTrackingSearchParams({ persisted: true });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reportIdentifier,
      reportColumns,
    },
  });

  const handleFormSubmit = React.useCallback((formData: FormSchema) => {
    const { reportColumns = [], reportIdentifier } = formData;
    updateReportColumns(reportColumns);
    updateReportIdentifier(reportIdentifier);
    onSubmit({
      reportColumns,
      reportIdentifier,
    });
  }, [onSubmit, updateReportIdentifier, updateReportColumns]);

  React.useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }), []);

  return (
    <Drawer.Root open={open} onOpenChange={setOpen}>
      <Drawer.Content>
        <div className={'max-w-[800px] w-full mx-auto'}>
          <Drawer.Header>
            <Drawer.Title className={'text-center'}>
              Refinar opções de relatório
            </Drawer.Title>
          </Drawer.Header>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleFormSubmit)}
              className={'flex flex-col gap-4 pb-10'}
            >
              <FormField
                control={form.control}
                name={'reportColumns'}
                render={({ field }) => (
                  <MultiSelectGs
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    label={'Colunas do relatório'}
                    options={reportColumnOptions}
                    maxCount={5}
                    modalPopover
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'reportIdentifier'}
                render={({ field }) => (
                  <InputGs {...field} type={'text'} label={'Identificador'} />
                )}
              />
              <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                {loading ? <ActivityIndicator /> : 'Requisitar relatório'}
              </Button.Root>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});